import { Platform } from 'react-native';
import Constants from 'expo-constants';
import { usersApi } from '@/lib/api';
import { hasExpoNativeModules } from './mobileNativeCapabilities';

const NATIVE_PUSH_PLATFORMS = new Set(['ios', 'android']);

// Android 1.0.5 (versionCode 110) does not ship expo-notifications. Importing
// that native module on those binaries crashes the process — blank screen on
// launch. 1.0.6+ includes the module and can register for push.
export const ANDROID_PUSH_MIN_NATIVE_VERSION = '1.0.6';

let rememberedPushToken = null;

export function getRememberedPushToken() {
  return rememberedPushToken;
}

export function rememberPushToken(token) {
  rememberedPushToken = token || null;
}

export function compareAppVersions(a, b) {
  const left = String(a || '').split('.').map((part) => parseInt(part, 10) || 0);
  const right = String(b || '').split('.').map((part) => parseInt(part, 10) || 0);
  const length = Math.max(left.length, right.length);
  for (let i = 0; i < length; i += 1) {
    const delta = (left[i] || 0) - (right[i] || 0);
    if (delta) return delta;
  }
  return 0;
}

export function nativeVersionSupportsPush(nativeVersion, runtimeVersion) {
  // Runtime IDs may be fingerprints or named native releases, not versions.
  // Only an actual version can establish support; opaque runtimes must never
  // override an old/unknown installed binary's missing-module protection.
  const isVersion = value => /^\d+\.\d+\.\d+(?:[-+].*)?$/.test(String(value || ''));
  if (nativeVersion && !isVersion(nativeVersion)) return false;
  const versions = [nativeVersion, runtimeVersion].filter(isVersion);
  if (!versions.length) return false;
  // Disable if the binary or this OTA runtime is older than 1.0.6. 1.0.5
  // devices can report a newer JS config version after an OTA; either
  // signal below 1.0.6 means expo-notifications is not in the binary.
  return versions.every((version) => (
    compareAppVersions(version, ANDROID_PUSH_MIN_NATIVE_VERSION) >= 0
  ));
}

function getRuntimeVersion() {
  const runtime = Constants.expoConfig?.runtimeVersion;
  return typeof runtime === 'string' ? runtime : null;
}

export function isNativePushPlatform(platform = Platform.OS) {
  if (!NATIVE_PUSH_PLATFORMS.has(platform)) return false;
  if (!nativeVersionSupportsPush(Constants.nativeAppVersion, getRuntimeVersion())) return false;
  // Version strings can come from an OTA. Inspect the installed module before
  // evaluating expo-notifications, especially on the original iOS 1.0.4 binary.
  // The package entry point eagerly loads ALL of these native adapters, even
  // when we only request a token. A partial module set must not import it.
  return hasExpoNativeModules([
    'ExpoPushTokenManager', 'ExpoNotificationPermissionsModule',
    'ExpoNotificationsEmitter', 'ExpoNotificationsHandlerModule',
    'ExpoBadgeModule', 'ExpoBackgroundNotificationTasksModule',
    'ExpoNotificationCategoriesModule', 'ExpoNotificationPresenter',
    'NotificationsServerRegistrationModule', 'ExpoNotificationScheduler', 'ExpoDevice',
    ...(platform === 'android' ? ['ExpoNotificationChannelGroupManager', 'ExpoNotificationChannelManager'] : []),
  ]);
}

function getEasProjectId() {
  return Constants.easConfig?.projectId
    || Constants.expoConfig?.extra?.eas?.projectId
    || null;
}

export function notificationPermissionGranted(settings, notifications) {
  return settings?.granted === true || settings?.status === 'granted'
    || (Platform.OS === 'ios' && settings?.ios?.status != null
      && settings.ios.status === notifications.IosAuthorizationStatus?.PROVISIONAL);
}

export async function getDeviceNotificationPermissions({ request = false, isCurrent = () => true } = {}) {
  if (!isCurrent() || !isNativePushPlatform()) return null;
  const Notifications = await import('expo-notifications');
  if (!isCurrent()) return null;
  if (request && Platform.OS === 'android') {
    await Notifications.setNotificationChannelAsync('default', {
      name: 'Default',
      importance: Notifications.AndroidImportance.DEFAULT,
    });
    await Notifications.setNotificationChannelAsync('quiet', {
      name: 'Quiet notifications',
      importance: Notifications.AndroidImportance.DEFAULT,
      sound: null,
      enableVibrate: false,
    });
  }

  const existing = await Notifications.getPermissionsAsync();
  if (!isCurrent()) return null;
  let settings = existing;
  if (request && !notificationPermissionGranted(settings, Notifications) && settings.canAskAgain !== false) {
    settings = await Notifications.requestPermissionsAsync({
      ios: { allowAlert: true, allowBadge: true, allowSound: true },
    });
  }
  return isCurrent() ? settings : null;
}

export async function registerDevicePushToken({ isCurrent = () => true, requestPermissions = true } = {}) {
  if (!isCurrent() || !isNativePushPlatform()) return null;
  const Device = await import('expo-device');
  if (!isCurrent() || !Device.isDevice) return null;
  const settings = await getDeviceNotificationPermissions({ request: requestPermissions, isCurrent });
  const Notifications = await import('expo-notifications');
  if (!isCurrent() || !notificationPermissionGranted(settings, Notifications)) return null;

  const projectId = getEasProjectId();
  if (!projectId) {
    console.warn('[push] missing EAS projectId; skip token registration');
    return null;
  }

  const tokenResult = await Notifications.getExpoPushTokenAsync({ projectId });
  const token = tokenResult?.data;
  if (!isCurrent() || !token) return null;

  rememberPushToken(token);
  await usersApi.registerPushToken({ token, platform: Platform.OS, isRequestCurrent: isCurrent });
  return isCurrent() ? token : null;
}

export async function unregisterRememberedPushToken() {
  const token = rememberedPushToken;
  rememberPushToken(null);
  if (!token) return;
  try {
    await usersApi.unregisterPushToken(token);
  } catch (error) {
    console.warn('[push] unregister failed', error?.message || error);
  }
}
